import React, { useEffect, useState } from "react";
import {
  Box,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Button,
  CircularProgress,
} from "@mui/material";
import { getLoggedInUserRole } from "../utils/auth";
import httpRequest from "../utils/httpRequest";

const InvoiceFilters = ({ filters, setFilters, onApply }) => {
  const role = getLoggedInUserRole();

  const [financialYears, setFinancialYears] = useState([]);
  const [firms, setFirms] = useState([]);
  const [loadingFY, setLoadingFY] = useState(false);
  const [loadingFirms, setLoadingFirms] = useState(false);
  const [isResetting, setIsResetting] = useState(false);

  useEffect(() => {
    let mounted = true;
    setLoadingFY(true);
    setLoadingFirms(true);

    Promise.all([
      httpRequest("invoice/getAllFinancialYears", "GET"),
      httpRequest("firm/getAll", "GET")
    ])
      .then(([fyRes, firmsRes]) => {
        if (mounted) {
          setFinancialYears(fyRes || []);
          setFirms(firmsRes || []);
        }
      })
      .catch((err) => {
        console.error("Failed to load data", err);
      })
      .finally(() => {
        if (mounted) {
          setLoadingFY(false);
          setLoadingFirms(false);
        }
      });

    return () => {
      mounted = false;
    };
  }, []);

  useEffect(() => {
    if (role === "auditor") {
      setFilters((prev) => ({
        ...prev,
        type: "gst",
      }));
    }
  }, [role, setFilters]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    const nextFilters = { ...filters, [name]: value };
    setFilters(nextFilters);
    onApply(nextFilters);
  };

  const handleKeyPress = (e) => {
    if (e.key === "Enter") {
      applyFilters(filters);
    }
  };

  const applyFilters = (filtersToApply) => {
    setFilters(filtersToApply);
    onApply(filtersToApply);
  };

  const resetFilters = () => {
    if (isResetting) return;
    
    setIsResetting(true);
    const empty = {
      invoice_number: "",
      customer_name: "",
      start_date: "",
      end_date: "",
      gstin: "",
      type: role === "auditor" ? "gst" : "",
      financial_year_id: "",
      firm_id: "",
    };
    setFilters(empty);
    onApply(empty);
    
    setTimeout(() => {
      setIsResetting(false);
    }, 500);
  };

  return (
    <Box
      component="form"
      display="flex"
      flexWrap="wrap"
      gap={2}
      alignItems="center"
      mb={3}
      mt={2}
      sx={{ width: "100%", justifyContent: "flex-start" }}
    >
      <TextField
        label="Invoice Number"
        name="invoice_number"
        value={filters.invoice_number || ""}
        onChange={handleChange}
        onKeyDown={handleKeyPress}
        size="small"
        sx={{ flex: "1 1 150px", minWidth: "120px" }}
      />
      <TextField
        label="Customer Name"
        name="customer_name"
        value={filters.customer_name || ""}
        onChange={handleChange}
        onKeyDown={handleKeyPress}
        size="small"
        sx={{ flex: "1 1 200px", minWidth: "150px" }}
      />
      <TextField
        label="Start Date"
        name="start_date"
        type="date"
        value={filters.start_date || ""}
        onChange={handleChange}
        onKeyDown={handleKeyPress}
        size="small"
        InputLabelProps={{ shrink: true }}
        sx={{ flex: "1 1 150px", minWidth: "120px" }}
      />
      <TextField
        label="End Date"
        name="end_date"
        type="date"
        value={filters.end_date || ""}
        onChange={handleChange}
        onKeyDown={handleKeyPress}
        size="small"
        InputLabelProps={{ shrink: true }}
        sx={{ flex: "1 1 150px", minWidth: "120px" }}
      />
      <TextField
        label="GSTIN"
        name="gstin"
        value={filters.gstin || ""}
        onChange={handleChange}
        onKeyDown={handleKeyPress}
        size="small"
        sx={{ flex: "1 1 150px", minWidth: "120px" }}
      />

      <FormControl size="small" sx={{ flex: "1 1 120px", minWidth: "100px" }}>
        <InputLabel id="type-label">Type</InputLabel>
        <Select
          labelId="type-label"
          name="type"
          value={filters.type || ""}
          onChange={handleChange}
          label="Type"
        >
          {role !== "auditor" && <MenuItem value="">All</MenuItem>}
          <MenuItem value="gst">GST</MenuItem>
          {role !== "auditor" && <MenuItem value="kachcha">Kachcha</MenuItem>}
          {role !== "auditor" && <MenuItem value="credit_note">Credit Note</MenuItem>}
          {role !== "auditor" && <MenuItem value="temp">Temp</MenuItem>}
        </Select>
      </FormControl>

      <FormControl
        size="small"
        sx={{ flex: "1 1 150px", minWidth: "120px" }}
        disabled={loadingFirms}
      >
        <InputLabel id="firm-label">Firm</InputLabel>
        <Select
          labelId="firm-label"
          name="firm_id"
          value={filters.firm_id || ""}
          onChange={handleChange}
          label="Firm"
        >
          <MenuItem value="">All</MenuItem>
          {loadingFirms ? (
            <MenuItem>
              <CircularProgress size={20} />
            </MenuItem>
          ) : (
            firms.map((firm) => (
              <MenuItem key={firm.id} value={firm.id}>
                {firm.name}
              </MenuItem>
            ))
          )}
        </Select>
      </FormControl>

      <FormControl
        size="small"
        sx={{ flex: "1 1 150px", minWidth: "120px" }}
        disabled={loadingFY}
      >
        <InputLabel id="fy-label">Financial Year</InputLabel>
        <Select
          labelId="fy-label"
          name="financial_year_id"
          value={filters.financial_year_id || ""}
          onChange={handleChange}
          label="Financial Year"
        >
          <MenuItem value="">All</MenuItem>
          {loadingFY ? (
            <MenuItem>
              <CircularProgress size={20} />
            </MenuItem>
          ) : (
            financialYears.map((fy) => (
              <MenuItem key={fy.id} value={fy.id}>
                {fy.description}
              </MenuItem>
            ))
          )}
        </Select>
      </FormControl>

      <Button 
        variant="outlined" 
        onClick={resetFilters} 
        disabled={(() => {
          const inv = filters?.invoice_number || "";
          const cust = filters?.customer_name || "";
          const sd = filters?.start_date || "";
          const ed = filters?.end_date || "";
          const gst = filters?.gstin || "";
          const t = filters?.type || "";
          const fy = filters?.financial_year_id || "";
          const fid = filters?.firm_id || "";
          const typeIsDefault = role === "auditor" ? (t === "" || t === "gst") : (t === "");
          const anyApplied = Boolean(
            inv.trim() ||
            cust.trim() ||
            sd ||
            ed ||
            gst.trim() ||
            !typeIsDefault ||
            fy ||
            fid
          );
          return isResetting || !anyApplied;
        })()}
        sx={{ flexShrink: 0 }}
      >
        {isResetting ? "Resetting..." : "Reset"}
      </Button>
    </Box>
  );
};

export default InvoiceFilters;
